import React, { useEffect, useState } from "react";
import "./Header2.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import { useUser } from "../../Context/UserContext";
import { Sections as OurStorySec } from "../../Pages/OurStory/OurStory";
import { Sections as ProductsSec } from "../../Pages/Products2/ProductsNew";
import { Sections as NewsSec } from "../../Pages/News/News";
import { Sections as InvestorSec } from "../../Pages/Investment/Investment";
import { Bell } from "lucide-react";
import { useModal } from "../../modal/modalContext";

interface NavItem {
  text: string;
  label?: React.ReactNode; // optional JSX
  link?: string;
  children?: NavItem[];
  permission?: string;
  classname?: string;
}

const Nav: React.FC = () => {
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const { user, hasPermission, bImpersonating } = useUser();
  const { logout } = useAuth();
  const hasNotification = Boolean(user?.messagecount && user?.messagecount > 0);
  const { openModal } = useModal();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Toggle of menu
  const handleToggle = () => {
    const menu_btn = document.getElementById("hamburger");
    const menu = document.getElementById("navbar");
    menu_btn?.classList.toggle("is-active");
    menu?.classList.toggle("is-active");
    setMenuOpen(!menuOpen);
  };

  // Top-level navigation click
  const handleLinkClick = (link?: string) => {
    setMenuOpen(false);
    setOpenDropdown(null);
    const menu_btn = document.getElementById("hamburger");
    const menu = document.getElementById("navbar");
    menu_btn?.classList.remove("is-active");
    menu?.classList.remove("is-active");
    if (link && link !== "#") navigate(link);
  };

  // Submenu item click
  const handleChildClick = (child?: NavItem) => {
    setMenuOpen(false);
    setOpenDropdown(null);
    if (child) {
      if (child.link && child.link !== "#") {
        navigate(child.link, {
          replace: child.link === routerLocation.pathname,
        });
      }
      if (child.text === "Log Out") {
        logout();
        navigate("/user-login");
      }
    }
  };

  const toggleDropdown = (text: string) => {
    const newValue = openDropdown === text ? null : text;
    setOpenDropdown(newValue);

    // If opening, wait for the CSS transition and then set data-open="true"
    if (newValue) {
      setTimeout(() => {
        const el = document.querySelector(
          `ul.dropdown.open`,
        ) as HTMLElement | null;
        if (el) {
          el.setAttribute("data-open", "true");
        }
      }, 300); // match your transition duration (0.3s)
    }
  };

  // Visibility & active checks
  const isVisible = (item: NavItem) =>
    !item.permission || hasPermission(item.permission);

  function isActive(item: NavItem): boolean {
    const currentPath = routerLocation.pathname;

    // Exact match
    if (item.link && currentPath === item.link) return true;

    // NEW: Match nested routes (example: /contact-management/Match)
    if (item.link && currentPath.startsWith(item.link + "/")) return true;

    // Don't highlight if on need-permission
    if (currentPath === "/need-permission") {
      return false;
    }

    // Bubble up from children
    if (item.children) {
      return item.children.some(isActive);
    }

    return false;
  }

  type NotificationBellProps = {
    hasUnread?: boolean;
    onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  };

  function NotificationBell({
    hasUnread = true,
    onClick,
  }: NotificationBellProps) {
    const messageCount = user?.messagecount ?? 0;
    return (
      <span
        className="bellWrapper"
        onClick={onClick}
        style={{ cursor: "pointer" }}
        title={`You have ${messageCount} message${messageCount > 1 ? "s" : ""}`}
      >
        <Bell size={18} />
        {hasUnread && <span className="bellDot" />}
      </span>
    );
  }

  const showMessages = () => {
    const userid = user?.id;
    openModal("notification", userid ? { userid } : { userid: "" });
  };

  //Build navigation
  const navConfig: NavItem[] = [
    { text: "Home", link: "/" },
    {
      text: "Our Story",
      link: "/ourstory",
      children: [
        ...OurStorySec.map((item) => ({
          text: item.label,
          link: `/${item.id}`,
        })),
      ],
    },
    { text: "Team", link: "/team" },
    getProductsMenu(user),
    getInvestorInfoMenu(user),
    getNewsMenu(user),
    getUserInfoMenu(user),
  ];

  if (user && hasPermission("admin_dashboard"))
    navConfig.splice(6, 0, {
      text: "My Tech",
      link: "/admin-dashboard",
      children: [
        {
          text: "User Management",
          link: "/user-management",
          permission: "admin_dashboard",
        },
        {
          text: "Role Admin Dashboard",
          link: "/user-admin",
          permission: "admin_dashboard",
        },
        {
          text: "Contract Status",
          link: "/user-management/ContractStatus",
          permission: "admin_dashboard",
        },
        {
          text: "User Activity",
          link: "/user-management/UserActivity",
          permission: "admin_dashboard",
        },
        {
          text: "User Notifications",
          link: "/user-management/UserNotifications",
          permission: "admin_dashboard",
        },
        {
          text: "Visitor Analytics",
          link: "/user-analytics",
          permission: "admin_dashboard",
        },
        {
          text: "Contact Management",
          link: "/contact-management",
          permission: "manage_contacts",
        },
        {
          text: "Reports",
          link: "/reports",
          permission: "admin_reports",
        },
      ],
    });

  function getNewsMenu(user: any): NavItem {
    return {
      text: "News",
      link: "/news",
      children: [
        ...NewsSec.map((item) => ({ text: item.label, link: `/${item.id}` })),
        !user || !hasPermission("news.Protected")
          ? { text: "Protected Info", link: "/need-permission" }
          : {
              text: "Protected Info",
              link: "/news-protected",
              permission: "news.Protected",
            },
      ],
    };
  }

  function getProductsMenu(user: any): NavItem {
    //|| !hasPermission("products.Protected") will probably need to add this back
    return {
      text: "Products",
      link: "/products",
      children: [
        ...ProductsSec.map((item) => ({
          text: item.label,
          link: `/${item.id}`,
        })),
        !user
          ? { text: "ESPEC", link: "/need-permission" }
          : { text: "ESPEC", link: "/espec" },
        !user
          ? { text: "Protected Info", link: "/need-permission" }
          : { text: "Protected Info", link: "/products-protected" },
      ],
    };
  }

  function getInvestorInfoMenu(user: any): NavItem {
    return {
      text: "Investor Info",
      link: "/investment",
      children: [
        ...InvestorSec.map((item) => ({
          text: item.label,
          link: `/${item.id}`,
        })),
        !user || !hasPermission("investor.Protected")
          ? { text: "Protected Info", link: "/need-permission" }
          : {
              text: "Protected Info",
              link: "/investor-protected",
              permission: "investor.Protected",
            },
      ],
    };
  }

  function getUserInfoMenu(user: any): NavItem {
    if (!user) {
      return {
        text: "User Info",
        children: [
          { text: "Sign Up", link: "/register" },
          { text: "Log In", link: "/user-login" },
          { text: "Request Info", link: "/request-info" },
        ],
      };
    } else {
      return {
        text: bImpersonating
          ? user.firstname + " (Impersonating)"
          : "My Account",
        label: (
          <div className="flex items-center gap-2">
            <span>My Account</span>
            {hasNotification && (
              <NotificationBell
                hasUnread={hasNotification}
                onClick={() => showMessages()}
              />
            )}
          </div>
        ),
        classname: "navItemWelcome",
        children: [
          { text: "Profile", link: "/profile" },
          { text: "Request Info", link: "/request-info" },

          {
            text: "Contracting",
            link: "/contracts-admin",
            permission: "admin_contracts",
          },
          {
            text: "My Agreements",
            link: "/agreements",
          },

          {
            text: "E-SPEC",
            link: "/espec-redirect",
            permission: "espec",
          },

          { text: "Log Out", link: "#" }, // No permission, always shown to authenticated user
        ],
      };
    }
  }

  return (
    <div className="navContainer">
      <nav id="navbar" className={`navbar ${menuOpen ? "is-active" : ""}`}>
        <ul>
          {navConfig.map((item, index) => (
            <li key={index}>
              <Link
                id={item.text.replace(/\s+/g, "")}
                to={item.link || "#"}
                className={`navItem ${
                  window.innerWidth >= 768 && isActive(item) ? "active" : ""
                }`}
                onClick={(e) => {
                  // Mobile + has children
                  if (item.children && menuOpen) {
                    // 👉 If we're already *on* that link, open the submenu instead of navigating
                    if (item.link === routerLocation.pathname) {
                      e.preventDefault();
                      toggleDropdown(item.text);
                      return;
                    }

                    // If the link is "#" (no real page), also open submenu
                    if (!item.link || item.link === "#") {
                      e.preventDefault();
                      toggleDropdown(item.text);
                      return;
                    }

                    // Otherwise navigate normally
                    handleLinkClick(item.link);
                  } else {
                    handleLinkClick(item.link);
                  }
                }}
                onMouseEnter={() => {
                  // Desktop hover opens submenu
                  if (window.innerWidth >= 768 && item.children) {
                    setOpenDropdown(item.text);
                  }
                }}
                onMouseLeave={() => {
                  if (window.innerWidth >= 768 && item.children) {
                    setOpenDropdown(null);
                  }
                }}
              >
                {item.label ?? item.text}
              </Link>
              {item.children && (
                <ul
                  className={`dropdown ${
                    openDropdown === item.text ? "open" : ""
                  }`}
                >
                  {item.children
                    .filter((child) => isVisible(child))
                    .map((child, subIndex) => (
                      <li key={subIndex}>
                        <Link
                          id={child.text.replace(/\s+/g, "")}
                          to={child.link || "#"}
                          className="navItem" // mobile submenu items always neutral
                          onClick={(e) => {
                            e.preventDefault();

                            handleChildClick(child); // normal navigation
                          }}
                        >
                          {child.text}
                        </Link>
                      </li>
                    ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>

      <button
        id="hamburger"
        className={`hamburger ${menuOpen ? "is-active" : ""}`}
        onClick={handleToggle}
      >
        <div className="bar"></div>
      </button>
    </div>
  );
};

export default Nav;
